/*
 * product-landing.js
 * -------------------
 * Self-contained behavior for the reusable `product-landing` section:
 *   - Hero gallery thumbnails
 *   - Consumption calculator (people -> recommended plan)
 *   - "Contratar" deep-links into the existing funnel (read-only, via URL params)
 *
 * Isolation: namespaced under window.CulliganLanding. Does NOT touch the
 * subscription funnel (sf-*) globals or the contact flow.
 */
(function () {
  'use strict';

  var Landing = (window.CulliganLanding = window.CulliganLanding || {});

  // --- Helpers ---------------------------------------------------------------

  // Parse the bottle count from a plan name like "Plan 3 Botellas/mes" -> 3
  function planBottles(plan) {
    if (plan == null) return null;
    if (plan.bottles != null && !isNaN(plan.bottles)) return Number(plan.bottles);
    var name = (plan.name || '') + ' ' + (plan.subtitle || '') + ' ' + (plan.description || '');
    var m = name.match(/(\d+)\s*botell/i);
    return m ? parseInt(m[1], 10) : null;
  }

  function formatEUR(value) {
    if (value == null || isNaN(value)) return '';
    try {
      return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'EUR',
      }).format(Number(value));
    } catch (e) {
      return Number(value).toFixed(2).replace('.', ',') + ' €';
    }
  }

  // A plan price may arrive as a number, a money string ("23,99 €") or cents.
  function planPriceNumber(plan) {
    if (plan == null || plan.price == null) return null;
    if (typeof plan.price === 'number') return plan.price;
    var cleaned = String(plan.price).replace(/[^0-9,.-]/g, '').replace(',', '.');
    var n = parseFloat(cleaned);
    return isNaN(n) ? null : n;
  }

  function setFunnelPlan(anchor, baseUrl, planId) {
    if (!anchor || !baseUrl) return;
    var sep = baseUrl.indexOf('?') === -1 ? '?' : '&';
    anchor.setAttribute(
      'href',
      planId != null ? baseUrl + sep + 'plan=' + encodeURIComponent(planId) : baseUrl
    );
  }

  // --- Gallery ---------------------------------------------------------------

  function initGallery(root) {
    var mainImg = root.querySelector('#pl-main-image');
    var thumbs = root.querySelectorAll('.pl-thumb');
    if (!mainImg || !thumbs.length) return;
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        var src = thumb.getAttribute('data-img');
        if (src) mainImg.src = src;
        thumbs.forEach(function (t) {
          t.classList.remove('is-active');
        });
        thumb.classList.add('is-active');
      });
    });
  }

  // --- Calculator ------------------------------------------------------------

  function initCalculator(root) {
    var section = root.querySelector('#pl-calculator');
    if (!section) return;

    var plans = [];
    try {
      plans = JSON.parse(section.getAttribute('data-plans') || '[]');
    } catch (e) {
      plans = [];
    }
    if (!Array.isArray(plans) || !plans.length) return;

    var perPerson = parseFloat(section.getAttribute('data-bottles-per-person'));
    if (isNaN(perPerson) || perPerson <= 0) perPerson = 1;

    var funnelBase = root.getAttribute('data-funnel-base') || '';

    var valueEl = section.querySelector('#pl-people-value');
    var minusEl = section.querySelector('#pl-people-minus');
    var plusEl = section.querySelector('#pl-people-plus');
    var planNameEl = section.querySelector('#pl-calc-plan-name');
    var planSubEl = section.querySelector('#pl-calc-plan-sub');
    var bottlesEl = section.querySelector('#pl-calc-bottles');
    var priceEl = section.querySelector('#pl-calc-price');
    var ctaEl = section.querySelector('#pl-calc-cta');

    // Sort plans ascending by their bottle count so we can pick the closest tier.
    var tiers = plans
      .map(function (p) {
        return { plan: p, bottles: planBottles(p) };
      })
      .filter(function (t) {
        return t.bottles != null;
      })
      .sort(function (a, b) {
        return a.bottles - b.bottles;
      });

    // Fallback: if no plan exposes a bottle count, just use plans in given order.
    var usingTiers = tiers.length > 0;

    function recommend(people) {
      var needed = Math.max(1, Math.round(people * perPerson));
      if (usingTiers) {
        // Smallest tier that covers the need; else the largest tier.
        var chosen = tiers[tiers.length - 1];
        for (var i = 0; i < tiers.length; i++) {
          if (tiers[i].bottles >= needed) {
            chosen = tiers[i];
            break;
          }
        }
        return { plan: chosen.plan, bottles: chosen.bottles };
      }
      var idx = Math.min(plans.length - 1, Math.max(0, needed - 1));
      return { plan: plans[idx], bottles: needed };
    }

    function render(people) {
      var rec = recommend(people);
      var plan = rec.plan || {};
      if (planNameEl) planNameEl.textContent = plan.name || '';
      if (planSubEl) planSubEl.textContent = plan.subtitle || '';
      if (bottlesEl) {
        bottlesEl.textContent =
          rec.bottles + (rec.bottles === 1 ? ' botella/mes' : ' botellas/mes');
      }
      if (priceEl) {
        var price = planPriceNumber(plan);
        priceEl.textContent =
          price != null ? formatEUR(price) : plan.price ? String(plan.price) : '';
      }
      setFunnelPlan(ctaEl, funnelBase, plan.id);
    }

    var people = parseInt(valueEl ? valueEl.textContent : '3', 10) || 3;

    function update(delta) {
      people = Math.min(12, Math.max(1, people + delta));
      if (valueEl) valueEl.textContent = String(people);
      render(people);
    }

    if (minusEl)
      minusEl.addEventListener('click', function () {
        update(-1);
      });
    if (plusEl)
      plusEl.addEventListener('click', function () {
        update(1);
      });

    render(people);
  }

  // --- Boot ------------------------------------------------------------------

  Landing.init = function () {
    var root = document.getElementById('pl-root');
    if (!root) return;
    initGallery(root);
    initCalculator(root);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', Landing.init);
  } else {
    Landing.init();
  }
})();
